import { get, ref, remove, set } from "firebase/database";
import { getPathDatabaseByRole } from "./auth";
import { db } from "../lib/firebase";
import { LaunchError } from "../utils/errors";
import { hashStringToID } from "../utils/string";
import {
  hydratePersistedEdl,
  serializeEdlForFirebase,
} from "../features/videoEditor/persistEdl";

export const getCreativesVideoEditedPath = ({ advertiserID, urlHash }) =>
  `creativesVideoEdited/${advertiserID}/${urlHash}`;

export const resolveCreativesVideoSourceUrl = ({
  sourceUrl = "",
  originalVideoUrl = "",
  url = "",
} = {}) => String(sourceUrl || originalVideoUrl || url || "").trim();

export const startGetCreativesVideoEdited =
  ({ sourceUrl }) =>
  async (dispatch) => {
    try {
      const { userID: advertiserID } = dispatch(getPathDatabaseByRole());
      const trimmedUrl = String(sourceUrl || "").trim();
      if (!advertiserID || !trimmedUrl) {
        return { ok: false, data: null };
      }

      const urlHash = hashStringToID(trimmedUrl);
      const snapshot = await get(
        ref(db, getCreativesVideoEditedPath({ advertiserID, urlHash }))
      );

      if (!snapshot.exists()) {
        return { ok: true, data: null, urlHash };
      }

      return { ok: true, data: snapshot.val(), urlHash };
    } catch (error) {
      dispatch(
        LaunchError(error, false, [], {
          functionName: "startGetCreativesVideoEdited",
        })
      );
      return { ok: false, data: null };
    }
  };

export const startSaveCreativesVideoEdited =
  ({ sourceUrl, url, subtitles, edl }) =>
  async (dispatch) => {
    try {
      const { userID: advertiserID } = dispatch(getPathDatabaseByRole());
      const trimmedSourceUrl = String(sourceUrl || "").trim();
      if (!advertiserID || !trimmedSourceUrl) {
        return { ok: false };
      }

      const urlHash = hashStringToID(trimmedSourceUrl);
      const nodeRef = ref(
        db,
        getCreativesVideoEditedPath({ advertiserID, urlHash })
      );
      const existingSnap = await get(nodeRef);
      const existing = existingSnap.exists() ? existingSnap.val() : {};
      const serializedEdl = edl
        ? serializeEdlForFirebase({ edl })
        : existing.edl || null;
      const now = Date.now();
      const next = {
        sourceUrl: trimmedSourceUrl,
        url: String(url || existing.url || trimmedSourceUrl).trim(),
        updateTime: now,
        creationTime: Number(edl?.creationTime) || existing.creationTime || now,
      };

      if (subtitles) {
        next.subtitles = subtitles;
      } else if (existing.subtitles) {
        next.subtitles = existing.subtitles;
      }

      if (serializedEdl) {
        next.edl = serializedEdl;
      }

      await set(nodeRef, next);

      const leftoverUrl = String(url || "").trim();
      if (leftoverUrl && leftoverUrl !== trimmedSourceUrl) {
        const leftoverHash = hashStringToID(leftoverUrl);
        if (leftoverHash !== urlHash) {
          await remove(
            ref(
              db,
              getCreativesVideoEditedPath({
                advertiserID,
                urlHash: leftoverHash,
              })
            )
          );
        }
      }

      return { ok: true, urlHash };
    } catch (error) {
      dispatch(
        LaunchError(error, false, [], {
          functionName: "startSaveCreativesVideoEdited",
        })
      );
      return { ok: false };
    }
  };

export const startHydrateCreativesVideoEdited =
  ({ sourceUrl, sourceAssetID, sourceName }) =>
  async (dispatch) => {
    const result = await dispatch(startGetCreativesVideoEdited({ sourceUrl }));
    if (!result?.ok || !result.data) {
      return {
        ok: Boolean(result?.ok),
        edl: null,
        snapshot: null,
        subtitles: null,
      };
    }

    const edl = hydratePersistedEdl({
      snapshot: result.data,
      sourceAssetID,
      sourceName,
    });

    return {
      ok: true,
      edl,
      snapshot: result.data,
      subtitles: result.data.subtitles || null,
      sourceUrl: resolveCreativesVideoSourceUrl(result.data),
    };
  };
